import {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {focusFormSection} from './formSectionFocus';
import {registerCommand} from './commandRegistry';
import {
	clearActionHighlight,
	existingFieldPlaces,
	expandExistingSection,
	highlightExistingField,
	highlightedFieldIsDisabled,
} from './actionHighlighter';
import {dispatchAgentCommand} from './commandDispatcher';
import {isLive, liveSession} from './sessionLifecycle';
import {endTurn, markTurn} from './turnTiming';
import {
	getDestination,
	getCompletionVerifier,
	getExperienceHandler,
	getFilterHandler,
	getDatasetHandler,
	getStatisticHandler,
	getFocusHandler,
	getModalHandler,
	getSectionHandler,
} from './resourcePluginRegistry';
import {registerBuiltInResourcePlugins} from '../resources/discovery';
import {getAgentPageContext} from './contextRegistry';
import {resolveEntities} from './resolveEntities';

/**
 * Wait until React Router and the destination page's own readiness condition
 * are both satisfied. The resource-specific readiness condition is supplied
 * by the owning plugin.
 * @param {string} destination allowlisted local route
 * @param {object} command navigation command
 * @param {Function|null} isResourceReady optional (command, context) => boolean
 * @param {number} timeoutMs maximum wait
 * @return {Promise<object>} the page context observed on arrival
 */
function waitForDestination(destination, command, isResourceReady, timeoutMs = 12000) {
	const startedAt = Date.now();
	// A readiness check may answer with a reason as well as a verdict, so a
	// page that arrived correctly but is missing the control the request
	// needs is not reported as a failure to navigate.
	let heldUpBy = null;
	return new Promise((resolve, reject) => {
		// A destination may carry a query string - the Client Dashboard names
		// which client that way, because that is how Canvas's own row click
		// addresses it - and arrival is a question about the route, not about
		// the parameters on it. Compared on the pathname alone; which record
		// arrived is the readiness check's job, and it has the command.
		const route = destination.split('?')[0].toLowerCase();
		const check = () => {
			const routeReady = window.location.pathname.toLowerCase() === route;
			const context = getAgentPageContext();
			const verdict = isResourceReady
				? isResourceReady(command, context)
				: true;
			const resourceReady = verdict === true || verdict?.ready === true;
			if (!resourceReady && verdict?.reason) heldUpBy = verdict.reason;
			if (routeReady && resourceReady) {
				// What was seen, not what was asked for. The page context is
				// read off the live page, so the record it names is the one
				// actually on screen - which is the difference between
				// evidence and an id repeated back.
				resolve(context);
				return;
			}
			if (Date.now() - startedAt >= timeoutMs) {
				reject(new Error(heldUpBy || 'navigation_not_ready'));
				return;
			}
			window.setTimeout(check, 50);
		};
		check();
	});
}

/**
 * What the page showed once it was ready, in the shape the backend weighs.
 *
 * `observed` is the page saying it looked: the route was compared against
 * the one asked for and the owning plugin's readiness check was consulted.
 * Without it every navigation stayed "unconfirmed" however well it worked,
 * and the reply hedged - "Canvas did not confirm which screen it landed
 * on" - about a screen that had in fact been verified.
 *
 * The record id is read from the live page context rather than echoed from
 * the command, so it can disagree, which is the only way it is worth
 * anything.
 *
 * @param {object} command navigation command
 * @param {object} context page context observed on arrival
 * @return {object} acknowledgement evidence
 */
export function observedOnArrival(command, context) {
	const kind = String(command.destination_id || '').split('.')[0];
	const seen = context && typeof context === 'object' ? context : {};
	const entityId = kind ? seen[`${kind}_id`] : null;
	return {
		observed: true,
		...(entityId ? {entity_id: String(entityId)} : {}),
	};
}

/**
 * Every control this setting could be, preferred first.
 *
 * A form renders one of several variants of a section depending on what the
 * device is, and the same setting is a different control in each. The agent
 * cannot know which variant is on screen; this page can, simply by looking.
 *
 * @param {object} field focus_field from the command
 * @return {Array<object>} candidate {name, path} places
 */
function placesFor(field) {
	return [
		{name: field.name, path: field.path},
		...(Array.isArray(field.alternatives) ? field.alternatives : []),
	].filter(
		(place) => place && (place.name || (place.path || []).length > 0),
	);
}

/**
 * Point at the field the request was answered by, once the record is open.
 *
 * Deliberately outside the navigation acknowledgement: the record asked for
 * has arrived either way, and a form that has not laid this field out yet -
 * or does not show it at all - must not turn a completed navigation into a
 * failed one. Retried briefly because a dashboard panel often mounts a beat
 * after the route does.
 *
 * Whether it was found is reported back, because Canvas shows many controls
 * only under some condition and the catalogue cannot know which. Saying
 * "taking you there" and then silently pointing at nothing leaves the user
 * hunting a form for something it is not showing them.
 *
 * @param {object|null} field focus_field named by the resource catalogue
 * @param {number} attemptsLeft remaining attempts
 * @param {string|null} modalId form identity used to scope section lookup
 * @return {Promise<boolean>} whether the field was reached
 */
function focusRequestedField(field, attemptsLeft = 20, modalId = null) {
	if (field?.kind === 'section') return focusFormSection(field.path, modalId);
	if (!field || (!field.name && !field.label)) return Promise.resolve(true);
	// Each candidate by its own control name first, which identifies it
	// exactly. The sections it sits inside are opened on the way: a
	// collapsed section hides the control entirely, and a section belonging
	// to a variant this device does not have simply is not there to open.
	const places = placesFor(field);
	// Sections first: a control inside a collapsed one is not there to find.
	const opened = places.some(
		(place) =>
			(place.path || []).length > 0 &&
			(place.path || []).every(expandExistingSection),
	);
	const found = places.some(
		(place) => place.name && highlightExistingField({name: place.name}),
	);
	// Then the visible label, which every variant shares. Tried last because
	// a name is exact and a label is only what the control is called - but
	// it is all there is where Canvas gives two controls the same identifier.
	if (found || highlightExistingField({label: field.label})) {
		return Promise.resolve(true);
	}
	// Nothing carries the label either, so a section named on its own was
	// the whole answer - a radio band has settings rather than being one.
	if (opened && places.every((place) => !place.name)) return Promise.resolve(true);
	if (attemptsLeft <= 1) return Promise.resolve(false);
	return new Promise((settle) => {
		window.setTimeout(
			() => settle(focusRequestedField(field, attemptsLeft - 1, modalId)),
			100,
		);
	});
}

/**
 * Installs the centralized allowlisted command bridge.
 * @param {object} props component properties
 * @param {React.ReactNode} props.children wrapped application content
 * @return {React.ReactNode} wrapped content
 */
export default function AgentBridgeProvider({children}) {
	const navigate = useNavigate();

	useEffect(() => {
		const unregisterPlugins = registerBuiltInResourcePlugins();
		const unregisterNavigate = registerCommand('navigate', async (command) => {
			const destination = getDestination(command.destination_id);
			if (!destination) throw new Error('unknown_destination');
			// A path may be built from the command, the way state already is.
			// Every destination until the Switch Dashboard had a fixed route
			// and carried which record it meant in router state; that one
			// names the switch in the path itself, by serial. Resolved once
			// so the wait watches the address we actually navigated to.
			const path = typeof destination.path === 'function'
				? destination.path(command)
				: destination.path;
			if (!path) throw new Error('unresolvable_destination_path');
			const state = destination.buildState
				? destination.buildState(command)
				: null;
			if (state) {
				navigate(path, {state});
			} else {
				navigate(path);
			}
			const arrived = await waitForDestination(
				path,
				command,
				getCompletionVerifier(`navigate:${command.destination_id}`) ||
					destination.isReady ||
					null,
			);
			const seen = observedOnArrival(command, arrived);
			const field = command.params?.focus_field;
			if (!field) return seen;
			// Whether the control was there has to travel back, the way it
			// does from a form. A setting that lives on a page rather than
			// in a modal - Canvas Settings puts its sections inline - was
			// arriving, pointing at nothing, and being reported as though
			// the request had been answered.
			const found = await focusRequestedField(field);
			const places = existingFieldPlaces(placesFor(field));
			return {
				...seen,
				focus_field_found: found,
				focus_field_disabled: found && highlightedFieldIsDisabled(),
				...(places.length > 1
					? {focus_field_places: places.map((place) => ({
						name: place.name,
						path: place.path || [],
					}))}
					: {}),
			};
		});
		const unregisterSection = registerCommand(
			'open_section',
			(command, envelope) => {
				const handler = getSectionHandler(command.section_id);
				if (!handler) throw new Error('unknown_section');
				return handler(command, envelope);
			},
		);
		const unregisterResolve = registerCommand(
			'resolve_entities',
			(command, envelope) => resolveEntities(
				command.params || {},
				getAgentPageContext(),
				envelope,
			),
		);
		const unregisterStatistic = registerCommand(
			'read_statistic',
			(command, envelope) => {
				const handler = getStatisticHandler(command.params?.metric);
				if (!handler) throw new Error('unknown_statistic');
				return handler(command, envelope);
			},
		);
		const unregisterDataset = registerCommand(
			'fetch_dataset',
			// The envelope carries the turn's abort signal, and this was the
			// one registration that dropped it - so the single handler that
			// makes a network request was the one the turn could not cancel.
			// Every sibling here already forwards it; this now does too.
			(command, envelope) => {
				const handler = getDatasetHandler(command.params?.source_id);
				if (!handler) throw new Error('unknown_dataset_source');
				return handler(command, envelope);
			},
		);
		const unregisterFilter = registerCommand(
			'apply_filter',
			(command, envelope) => {
				const handler = getFilterHandler(command.params?.resource);
				if (!handler) throw new Error('unknown_filter');
				return handler(command, envelope);
			},
		);
		const unregisterFocus = registerCommand(
			'focus_entity',
			(command, envelope) => {
				const handler = getFocusHandler(command.params?.resource);
				if (!handler) throw new Error('unknown_focus_handler');
				return handler(command, envelope);
			},
		);
		// An unknown id is still refused synchronously - callers rely on it -
		// so only what happens after the handler resolves is deferred. Most
		// settings live inside the form rather than on the page it opens
		// over, so the field is pointed at once the form is there rather
		// than when the route changed.
		// The outcome travels back with the command's own completion event,
		// which is what the "the form is open" reply is written from - so
		// that reply can say when the form is not showing the field.
		const openThenFocus = (open, command, envelope) =>
			Promise.resolve(open(command, envelope)).then(async (opened) => {
				const field = command.params?.focus_field;
				if (!field) return opened;
				const found = await focusRequestedField(field, 20, command.modal_id);
				// A form can lay the same setting out in two places at once -
				// an upstream and a downstream copy of the same address. Only
				// the rendered form knows when that happened, so the places it
				// really shows travel back and the choice is put to the user
				// rather than made here by taking whichever came first.
				const places = existingFieldPlaces(placesFor(field));
				// Shown but greyed out is a third outcome, and a different
				// answer: the setting is there, and this record cannot change
				// it. Reporting it as missing sent the user looking for
				// something already on their screen.
				return {...(opened && typeof opened === 'object' ? opened : {}),
					focus_field_found: found,
					focus_field_disabled: found && highlightedFieldIsDisabled(),
					...(places.length > 1
						? {focus_field_places: places.map((place) => ({
							name: place.name,
							path: place.path || [],
						}))}
						: {})};
			});
		const unregisterModal = registerCommand(
			'open_modal',
			(command, envelope) => {
				const handler = getModalHandler(command.modal_id);
				if (!handler) throw new Error('unknown_modal');
				return openThenFocus(handler, command, envelope);
			},
		);
		const unregisterExperience = registerCommand(
			'open_experience',
			(command, envelope) => {
				const handler = getExperienceHandler(command.experience_id);
				if (!handler) throw new Error('unknown_experience');
				return openThenFocus(handler, command, envelope);
			},
		);
		return () => {
			unregisterNavigate();
			unregisterSection();
			unregisterResolve();
			unregisterFilter();
			unregisterStatistic();
			unregisterDataset();
			unregisterFocus();
			unregisterModal();
			unregisterExperience();
			unregisterPlugins();
		};
	}, [navigate]);

	useEffect(() => {
		const onResponse = async (event) => {
			let response = event.detail?.response;
			const sessionId = event.detail?.sessionId;
			// Which conversation is live is not this component's to decide.
			// It used to be: this read the id off whatever response arrived
			// and treated that as current, so a reply already in flight when
			// the user reset the chat would land here afterwards and
			// reinstate the conversation they had just left.
			//
			// Now the lifecycle is told by the events that actually change
			// it - a reset, a thread switch - and this only asks.
			// The generation the *request* was issued in, not the one current
			// when its reply arrived.
			//
			// Reading it here made every arriving response look current by
			// construction: a reply that crossed a reset picked up the new
			// generation on the way in and passed its own check. The hook
			// stamps the generation when it sends, and that is the number
			// that says whether this work is still wanted.
			const issuedIn = event.detail?.sessionGeneration
				?? liveSession().generation;
			if (!isLive(sessionId, issuedIn)) return;
			// Came back on the reply, so it survives this whole chain without
			// the page holding a "current turn" of its own.
			const turnId = response?.turn_id;
			let executedCommand = false;
			// A fresh piece of work starts here, so a control marked for the
			// last one stops being marked. Otherwise an outline sat on a
			// button long after the request that pointed at it had finished.
			if (response?.commands?.length) clearActionHighlight();
			while (response?.commands?.length) {
				// Checked between commands, not only before the first. A
				// reset that arrives midway through a chain must stop the
				// rest of it rather than letting the chain finish into a
				// conversation nobody is looking at.
				if (!isLive(sessionId, issuedIn)) return;
				executedCommand = true;
				response = await dispatchAgentCommand(response.commands[0], {
					request_id: response.request_id,
					session_id: sessionId,
					turn_id: turnId,
					// Read at dispatch rather than captured at the top of the
					// loop: a conversation can be reset while a chain of
					// commands is still running, and the point of the guard
					// is to notice that between one command and the next.
					active_session_id: liveSession().sessionId,
					// The generation the work was issued in. An id can
					// repeat - a thread switched away from and back to is
					// the same id and a different stretch of work - so the
					// id alone cannot say whether this is still wanted.
					session_generation: issuedIn,
				});
			}
			// Nothing further is pending: whatever the user asked for has
			// either happened or failed, and this is what they waited for.
			markTurn(turnId, 'commands_done_ms');
			endTurn(turnId);
			// The chat request path renders the initial response. Only publish
			// a bridge reply when command acknowledgements produced a new,
			// follow-up response; otherwise the same bubble appears twice.
			if (executedCommand && response?.message) {
				window.dispatchEvent(new CustomEvent('canvas-agent:reply', {
					detail: {response, sessionId},
				}));
			}
		};
		window.addEventListener('canvas-agent:response', onResponse);
		return () => window.removeEventListener('canvas-agent:response', onResponse);
	}, []);

	return children;
}
